'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/tooltip/styles.css';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { delegateTooltips } from 'BomdisoftComponents/tooltip/delegate';
import { hasTooltipTitle, TOOLTIP_TRIGGER_ATTRIBUTE, type TooltipPlacement } from 'BomdisoftComponents/tooltip/render';
import { type ReactNode, useId } from 'react';

export type { TooltipPlacement } from 'BomdisoftComponents/tooltip/render';

export type TooltipProps = {
  children: ReactNode;
  title: ReactNode;
  placement?: TooltipPlacement;
  unstyled?: boolean;
  className?: string;
  /** Makes the anchor fill its container, for a child that sizes itself to the
   * row (a `MenuItem`, an action row). Off by default because the anchor is
   * inline by nature and most triggers are an icon or a word. `className`
   * reaches the bubble, never the anchor, so this is the only way to say it. */
  fill?: boolean;
  testId?: string;
};

const delegateFromTrigger = (trigger: HTMLSpanElement | null) => {
  const root = trigger?.getRootNode();
  if (root !== undefined && (root.nodeType === root.DOCUMENT_NODE || root.nodeType === root.DOCUMENT_FRAGMENT_NODE)) delegateTooltips(root as Document | ShadowRoot);
};

export const Tooltip = ({ children, title, placement = 'top', unstyled, className, fill, testId }: TooltipProps) => {
  const id = useId();

  if (!hasTooltipTitle(title)) return <>{children}</>;

  return (
    <>
      <span className={joinClasses('bomdisoft-tooltip-trigger', fill && 'bomdisoft-tooltip-trigger-fill')} {...{ [TOOLTIP_TRIGGER_ATTRIBUTE]: placement }} data-testid={testId} ref={delegateFromTrigger}>
        {children}
      </span>
      <div className={joinClasses('bomdisoft-tooltip', !unstyled && 'bomdisoft-tooltip-chrome', className)} data-placement={placement} id={id} popover='manual' role='tooltip'>
        {title}
        {unstyled ? null : <span aria-hidden='true' className='bomdisoft-tooltip-arrow' />}
      </div>
    </>
  );
};
